import React, { useEffect, useState } from 'react';
import {
  Grid,
  TextField,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import ResponsiveDialog from '../../../../components/molecules/responsiveDialog';
import { Title } from '../../../../components/atoms/title';
import api from '../../../../services/api';
import { messageTemplates } from '../../utils/messageTemplates';

export function NinaMessage({ userData, setMessage, message, issueType, issueId, setIsDefaultMessage }) {
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogContent, setErrorDialogContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [selectedIssueType, setSelectedIssueType] = useState(issueType);
  const [selectedMessage, setSelectedMessage] = useState('');
  const [isCustomMessage, setIsCustomMessage] = useState(false);

  const name = userData?.user.first_name;
  const platform = userData?.user.platform;

  const templates = messageTemplates({ name, platform, issueId, countryId: userData?.user.country_id });

  useEffect(() => {
    const defaultApprovedMessage = templates[selectedIssueType]?.find(
      template => template.id === 'defaultApproved'
    )?.message;

    if (defaultApprovedMessage && issueId) {
      setSelectedMessage(defaultApprovedMessage);
      setMessage(defaultApprovedMessage);
    } else {
      setSelectedMessage('');
      setMessage('');
    }
  }, [selectedIssueType, name, platform, issueId]);

  useEffect(() => {
    if (isCustomMessage) {
      setIsDefaultMessage(false);
    }
  }, [isCustomMessage, setIsDefaultMessage]);

  const handleSelectIssueType = event => {
    setSelectedIssueType(event.target.value);
  };

  const handleSelectMessage = event => {
    setSelectedMessage(event.target.value);
    setMessage(event.target.value);
    setIsCustomMessage(false);

    const isDefault = templates[selectedIssueType].some(
      template => template.message === event.target.value && template.id
    );
    setIsDefaultMessage(isDefault);
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    getValues,
  } = useForm();

  const handleSendMessage = async data => {
    const body = {
      emails: [userData?.user.email],
      message: data.message,
    };

    const route = '/admin/notifications/some';

    setIsLoading(true);

    try {
      await api.post(route, body);

      const toastMessage = 'Mensagem enviada com sucesso!';

      reset();

      toast(toastMessage, { type: 'success' });
    } catch (error) {
      setErrorDialogOpen(prevState => !prevState);
      setErrorDialogContent(
        error.response?.data.error?.message?.match(/Notification in progress/)
          ? 'Uma notificação está em progresso. Aguarde até o final para enviar outra.'
          : 'Algum erro ocorreu no envio dessa mensagem.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseConfirmationDialog = () => {
    setConfirmationDialogOpen(prevState => !prevState);
  };

  const onSubmit = () => {
    setConfirmationDialogOpen(prevState => !prevState);
  };

  const issueTypes = Object.keys(templates);
  const issueTypeLabels = {
    payment: 'Pagamento',
    bug: 'Bug',
    'info change': 'Alteração de informações',
  };

  return (
    <Grid style={{ marginBottom: '16px' }}>
      <Grid container justifyContent='space-between'>
        <Grid item>
          <Title>Mensagem da Nina</Title>
        </Grid>
        {issueType && (
          <Grid item container xs={6} spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id='select-template'>Tipo de template</InputLabel>
                <Select
                  value={selectedIssueType}
                  onChange={handleSelectIssueType}
                  label='Tipo de template'
                  labelId='select-template'
                  style={{ height: '40px' }}
                >
                  {issueTypes.map(type => (
                    <MenuItem key={type} value={type}>
                      {issueTypeLabels[type]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id='select-message'>Mensagem</InputLabel>
                <Select
                  value={isCustomMessage ? 'Customizada' : selectedMessage}
                  onChange={handleSelectMessage}
                  label='Mensagem'
                  labelId='select-message'
                  style={{ height: '40px' }}
                >
                  {templates[selectedIssueType]?.map(template => (
                    <MenuItem key={selectedIssueType + template.name} value={template.message}>
                      {template.name}
                    </MenuItem>
                  ))}
                  {isCustomMessage && <MenuItem value='Customizada'>Customizada</MenuItem>}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Grid>
      <Grid xs={12} item>
        <TextField
          required
          id='message'
          name='message'
          label='Mensagem'
          fullWidth
          margin='dense'
          {...register('message')}
          multiline
          minRows={4}
          maxRows={15}
          variant='outlined'
          value={message}
          onChange={e => {
            setMessage(e.target.value);
            setIsCustomMessage(true);
          }}
        />
      </Grid>
      {!issueType && (
        <>
          <Grid xs={12} item container justifyContent='center'>
            <Typography variant='inherit' color='textSecondary'>
              {errors.message?.message}
            </Typography>
          </Grid>
          <Grid xs={12} item container justifyContent='center'>
            <Button
              onClick={handleSubmit(onSubmit)}
              variant='contained'
              color='primary'
              style={{ marginTop: '16px' }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Enviar mensagem da Nina'}
            </Button>
          </Grid>

          <Dialog
            open={confirmationDialogOpen}
            onClose={handleCloseConfirmationDialog}
            aria-labelledby='alert-dialog-title'
            aria-describedby='alert-dialog-description'
          >
            <DialogTitle id='alert-dialog-title'>Confirmar envio da mensagem?</DialogTitle>
            <DialogContent>
              <DialogContentText id='alert-dialog-description'>
                Tem certeza que deseja enviar essa mensagem?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseConfirmationDialog}>Não</Button>
              <Button
                onClick={() => {
                  handleSendMessage({ message: getValues('message') });
                  handleCloseConfirmationDialog();
                }}
                autoFocus
              >
                Sim
              </Button>
            </DialogActions>
          </Dialog>

          <ResponsiveDialog
            open={errorDialogOpen}
            handleClose={() => {
              setErrorDialogOpen(prevState => !prevState);
            }}
            title='Erro no envio'
            type='blue'
            content={errorDialogContent}
          />
        </>
      )}
    </Grid>
  );
}
